import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlatformInfo } from '../../utils/platformUtils';
import { PermissionChecker, PermissionInfo, PermissionStatusType } from '../../services/permissionChecker';

/**
 * 权限总览组件
 * 显示当前平台的所有权限状态
 */
export const PermissionOverview: React.FC = () => {
  const { t } = useTranslation(['v3/developerSettings']);
  const [permissions, setPermissions] = useState<PermissionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  
  const platformInfo = getPlatformInfo();

  /**
   * 检查所有权限状态
   */
  const checkAllPermissions = async () => {
    setIsLoading(true);
    try {
      const results = await PermissionChecker.getAllPermissions();
      setPermissions(results);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('[PermissionOverview] Error checking permissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 组件挂载时自动检查权限
   */
  useEffect(() => {
    checkAllPermissions();
  }, []);

  /**
   * 获取平台描述文本
   */
  const getPlatformDescription = (): string => {
    const parts = [platformInfo.os];
    
    if (platformInfo.isNative) {
      parts.push('原生应用');
    } else if (platformInfo.isElectron) {
      parts.push('桌面应用');
    } else if (platformInfo.isPWA) {
      parts.push('PWA');
    } else {
      parts.push('网页版');
    }

    return parts.join(' - ');
  };

  /**
   * 获取权限状态的颜色类
   */
  const getStatusColorClass = (status: PermissionStatusType): string => {
    switch (status) {
      case 'granted':
        return 'text-green-600 dark:text-green-400';
      case 'denied':
        return 'text-red-600 dark:text-red-400';
      case 'prompt':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'unsupported':
        return 'text-ios-gray dark:text-ios-dark-gray';
      case 'checking':
        return 'text-blue-600 dark:text-blue-400';
      case 'error':
        return 'text-orange-600 dark:text-orange-400';
      default:
        return 'text-ios-gray dark:text-ios-dark-gray';
    }
  };

  /**
   * 格式化时间显示
   */
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-ios-headline font-medium">
          {t('permissions.title', '客户端权限总览')}
        </h3>
        <button
          onClick={checkAllPermissions}
          disabled={isLoading}
          className="text-ios-blue dark:text-ios-dark-blue text-ios-subhead disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? t('permissions.checking', '检查中...') : t('permissions.refresh', '刷新')}
        </button>
      </div>
      
      <div className="bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md p-3">
        {/* 平台信息提示 */}
        <div className="mb-3 text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
          <div className="flex items-center justify-between">
            <span>
              {t('permissions.currentPlatform', '当前平台')}: {getPlatformDescription()}
            </span>
            {lastRefresh && (
              <span className="text-ios-caption">
                {t('permissions.lastRefresh', '最后刷新')}: {formatTime(lastRefresh)}
              </span>
            )}
          </div>
        </div>

        {/* 权限列表 */}
        <div className="space-y-2">
          {permissions.length === 0 && !isLoading ? (
            <div className="text-center text-ios-gray dark:text-ios-dark-gray py-4">
              {t('permissions.noPermissions', '暂无权限信息')}
            </div>
          ) : (
            permissions.map((permission) => (
              <div key={permission.key} className="flex items-center justify-between">
                <div className="flex items-center space-x-2 flex-1">
                  <span className="text-lg">
                    {PermissionChecker.getPermissionStatusIcon(permission.status)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-ios-subhead font-medium">
                        {permission.name}
                      </span>
                      {!permission.supported && (
                        <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                          ({t('permissions.unsupportedPlatform', '此平台不支持')})
                        </span>
                      )}
                    </div>
                    <div className="text-ios-caption text-ios-gray dark:text-ios-dark-gray truncate">
                      {permission.description}
                    </div>
                    {permission.error && (
                      <div className="text-ios-caption text-red-600 dark:text-red-400 truncate">
                        {t('permissions.error', '错误')}: {permission.error}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right ml-2">
                  <div className={`text-ios-footnote font-medium ${getStatusColorClass(permission.status)}`}>
                    {PermissionChecker.getPermissionStatusText(permission.status)}
                  </div>
                  {permission.lastChecked && (
                    <div className="text-ios-caption text-ios-gray dark:text-ios-dark-gray">
                      {formatTime(permission.lastChecked)}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* 权限说明 */}
        <div className="mt-3 pt-3 border-t border-ios-gray5 dark:border-ios-dark-gray4">
          <div className="text-ios-caption text-ios-gray dark:text-ios-dark-gray space-y-1">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>✅ {t('permissions.status.granted', '已授权')}</span>
              <span>❌ {t('permissions.status.denied', '已拒绝')}</span>
              <span>❓ {t('permissions.status.prompt', '待询问')}</span>
              <span>➖ {t('permissions.status.unsupported', '不支持')}</span>
              <span>🔄 {t('permissions.status.checking', '检查中')}</span>
              <span>{t('permissions.status.errorWithIcon', '⚠️ 错误')}</span>
            </div>
            <div className="mt-2">
              {t('permissions.note', '某些权限需要用户主动触发功能时才会请求（如麦克风、相机）')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionOverview;
